import React, { useState, useMemo, useEffect, useRef } from "react";
import { ChevronRightIcon, ChevronDownIcon, ChartBarIcon } from "../icons";
import { PublicOfferData } from "./BaseTemplate";

interface PublicFinancialSummaryProps {
  financialSummary: NonNullable<PublicOfferData["financialSummary"]>;
  template?: string;
}

const PublicFinancialSummary: React.FC<PublicFinancialSummaryProps> = ({
  financialSummary,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: "PLN",
      currencyDisplay: "code",
    }).format(amount);
  };

  // Funkcja formatująca nazwę pozycji z informacją o liczbie osób (tylko dla noclegów)
  const formatItemName = (item: any) => {
    const baseName =
      item.customTitle && item.categoryOptionName
        ? `${item.customTitle} (${item.categoryOptionName})`
        : item.customTitle || item.categoryOptionName || "Brak opisu";

    // Dodaj informację o liczbie osób tylko dla noclegów gdzie jest różnica
    if (
      item.personsPerRoom &&
      item.personsCountPerRoom &&
      item.personsPerRoom !== item.personsCountPerRoom
    ) {
      return `${baseName} (dla ${item.personsCountPerRoom} os.)`;
    }

    return baseName;
  };

  const [expandedGroups, setExpandedGroups] = useState<{
    [key: string]: boolean;
  }>({});

  const savedStateRef = useRef<{ [key: string]: boolean }>({});

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Print event listeners to automatically expand all groups during printing
  useEffect(() => {
    const handleBeforePrint = () => {
      // Save current state
      savedStateRef.current = { ...expandedGroups };

      // Expand all groups for printing
      const allExpanded: { [key: string]: boolean } = {};
      Object.keys(financialSummary.groupedByVatAndCategory)
        .filter((key) => financialSummary.groupedByVatAndCategory[key].net > 0)
        .forEach((key) => {
          allExpanded[key] = true;
        });

      setExpandedGroups(allExpanded);
    };

    const handleAfterPrint = () => {
      // Restore previous state
      setExpandedGroups(savedStateRef.current);
    };

    // Add event listeners
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [financialSummary.groupedByVatAndCategory, expandedGroups]);

  // Use the new backend structure directly
  const totalNetto = financialSummary.totalNet;
  const totalVat = financialSummary.totalVat;
  const totalBrutto = financialSummary.totalGross;
  const totalWith23Vat = financialSummary.totalWith23Vat;

  // Extract discount information
  const appliedDiscounts = financialSummary.appliedDiscounts || {};
  const totalDiscountAmount = appliedDiscounts.totalDiscountAmount || 0;
  // Named boolean derived once; drives the "RAZEM PO RABATACH" visibility and the
  // "RAZEM:" row styling (struck-through "before" total vs. promoted final total).
  const hasDiscount = totalDiscountAmount > 0;

  // Calculate original total before discounts
  const originalTotal = totalNetto + totalDiscountAmount;

  // Calculate original VAT before discounts (sum of VAT from all categories according to their actual rates)
  const originalTotalVat = useMemo(() => {
    return Object.values(financialSummary.groupedByVatAndCategory).reduce(
      (sum, group) => sum + group.originalNet * (group.vatRate / 100),
      0
    );
  }, [financialSummary.groupedByVatAndCategory]);

  // Calculate original gross before discounts
  const originalTotalGross = originalTotal + originalTotalVat;

  // Calculate accommodation original amount for global discounts
  const accommodationOriginalNet = useMemo(() => {
    const accommodationGroups = Object.values(
      financialSummary.groupedByVatAndCategory
    ).filter(
      (group) =>
        group.categoryName.toLowerCase().includes("noclegi") ||
        group.categoryName.toLowerCase().includes("accommodation")
    );
    return accommodationGroups.reduce(
      (sum, group) => sum + group.originalNet,
      0
    );
  }, [financialSummary.groupedByVatAndCategory]);

  return (
    <div className="mb-8" data-section="financial-summary">
      {/* Header */}
      <div className="mb-6">
        <h2
          className="text-xl font-semibold mb-4 flex items-center gap-2"
          style={{ color: "var(--gradient-start, #334155)" }}
        >
          <ChartBarIcon
            className="w-5 h-5"
            style={{ color: "var(--gradient-start, #64748b)" }}
          />
          Podsumowanie Finansowe
        </h2>
      </div>

      {/* VAT breakdown table */}
      {totalNetto > 0 &&
        Object.keys(financialSummary.groupedByVatAndCategory).filter(
          (key) => financialSummary.groupedByVatAndCategory[key].net > 0
        ).length > 0 && (
          <div className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">
                Szczegółowy podział według kategorii i stawek VAT
              </h3>
            </div>

            {/* Table header - UPDATED LAYOUT: chevron on left */}
            <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-sm font-semibold text-slate-700 bg-slate-50 border-b border-slate-200">
              <div className="p-4 text-center"></div>
              <div className="p-4 text-left">Kategoria usługi</div>
              <div className="p-4 text-right">Netto</div>
              <div className="p-4 text-right">VAT</div>
              <div className="p-4 text-right">Brutto</div>
            </div>

            {/* Table rows */}
            {Object.entries(financialSummary.groupedByVatAndCategory)
              .filter(([_, group]) => group.net > 0)
              .map(([groupKey, group]) => {
                const hasDiscount = group.discountAmount > 0;

                const discountPercent =
                  group.originalNet > 0
                    ? Math.round(
                        (group.discountAmount / group.originalNet) * 1000
                      ) / 10
                    : 0;

                return (
                  <div key={groupKey}>
                    {/* Combined category block with main row and discount row */}
                    <div
                      className={`${hasDiscount ? "border-b border-slate-200" : "border-b border-slate-100"} hover:bg-slate-50 cursor-pointer transition-colors`}
                      onClick={() => toggleGroup(groupKey)}
                    >
                      {/* Main category row - shows price BEFORE discount */}
                      <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-sm">
                        <div className="p-4 flex justify-center">
                          {expandedGroups[groupKey] ? (
                            <ChevronDownIcon className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5 text-slate-500" />
                          )}
                        </div>
                        <div className="p-4">
                          <div className="font-semibold text-slate-900 text-left">
                            {group.categoryName} {group.vatRate}%
                          </div>
                        </div>
                        <div
                          className={`p-4 text-right ${hasDiscount ? "text-slate-500 line-through" : "text-slate-600"}`}
                        >
                          {formatCurrency(group.originalNet)}
                        </div>
                        <div
                          className={`p-4 text-right ${hasDiscount ? "text-slate-500 line-through" : "text-slate-700"}`}
                        >
                          {formatCurrency(
                            group.originalNet * (group.vatRate / 100)
                          )}
                        </div>
                        <div
                          className={`p-4 text-right ${hasDiscount ? "text-slate-500 line-through" : "text-slate-900"}`}
                        >
                          {formatCurrency(
                            group.originalNet * (1 + group.vatRate / 100)
                          )}
                        </div>
                      </div>

                      {/* Discount row - shows ONLY when there is a discount, visually connected */}
                      {hasDiscount && (
                        <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-sm bg-green-50">
                          <div className="p-4"></div>
                          <div className="p-4 pl-8">
                            <div className="text-green-700 font-medium text-left">
                              └─ Rabat {discountPercent}% (-
                              {formatCurrency(group.discountAmount)})
                            </div>
                          </div>
                          <div className="p-4 text-right text-slate-900">
                            {formatCurrency(group.net)}
                          </div>
                          <div className="p-4 text-right text-slate-700">
                            {formatCurrency(group.vat)}
                          </div>
                          <div className="p-4 text-right text-slate-900">
                            {formatCurrency(group.gross)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Expanded details */}
                    {expandedGroups[groupKey] && (
                      <div className="bg-slate-50 border-b border-neutral-100">
                        <div className="p-3">
                          {/* Items table header */}
                          <div className="grid grid-cols-[40px_2fr_60px_100px_100px_100px] text-xs font-medium text-slate-700 mb-2 border-b border-slate-200 pb-1">
                            <div className="text-left">Lp.</div>
                            <div className="text-left">Pozycja</div>
                            <div className="text-right">Ilość</div>
                            <div className="text-right">Cena jedn. netto</div>
                            <div className="text-right">Wartość netto</div>
                            <div className="text-right">Brutto</div>
                          </div>

                          {/* Items table rows - NEW LAYOUT with discount rows */}
                          <div className="space-y-1">
                            {group.items
                              .filter((item) => {
                                const unitPrice = parseFloat(
                                  item.unitPrice || "0"
                                );
                                const finalPrice = parseFloat(
                                  item.finalPrice || "0"
                                );
                                // Show item if either unitPrice > 0 OR finalPrice > 0
                                return unitPrice > 0 || finalPrice > 0;
                              })
                              .map((item, index) => {
                                const unitPrice = parseFloat(
                                  item.unitPrice || "0"
                                );
                                const quantity = item.quantity || 1;
                                const baseItemPrice = unitPrice * quantity;

                                // Calculate discount amount for display FIRST
                                let discountAmount = 0;
                                let discountDescription = "";
                                if (item.discount && item.discount.value) {
                                  const discountValue = parseFloat(
                                    item.discount.value.toString()
                                  );
                                  if (discountValue > 0) {
                                    if (item.discount.type === "percentage") {
                                      discountAmount =
                                        (baseItemPrice * discountValue) / 100;
                                      discountDescription = `Rabat ${discountValue}% (-${formatCurrency(discountAmount)})`;
                                    } else if (
                                      item.discount.type === "amount"
                                    ) {
                                      discountAmount = discountValue * quantity;
                                      discountDescription = `Rabat ${formatCurrency(discountValue)}/szt. (-${formatCurrency(discountAmount)})`;
                                    }
                                  }
                                }

                                // POPRAWKA: finalPrice z backend to już cena całkowita pozycji (nie mnożymy przez quantity)
                                const finalPriceRaw = parseFloat(
                                  item.finalPrice || "0"
                                );

                                // POPRAWKA: Jeśli finalPrice nie jest dostępne, użyj ceny po rabacie pozycji
                                const totalItemPrice =
                                  finalPriceRaw > 0
                                    ? finalPriceRaw
                                    : baseItemPrice - discountAmount;

                                // If no discount from discount object, fallback to price difference
                                if (discountAmount === 0) {
                                  discountAmount =
                                    baseItemPrice - totalItemPrice;
                                  if (discountAmount > 0) {
                                    discountDescription = `Rabat (-${formatCurrency(discountAmount)})`;
                                  }
                                }

                                const unitPriceNet = totalItemPrice / quantity;
                                const grossItemPrice = totalItemPrice * (1 + group.vatRate / 100);

                                return (
                                  <div key={index} className="space-y-1">
                                    {/* Basic item row */}
                                    <div className="grid grid-cols-[40px_2fr_60px_100px_100px_100px] text-xs text-slate-800 py-1">
                                      <div className="text-left text-slate-600 font-medium">
                                        {index + 1}.
                                      </div>
                                      <div className="text-left">
                                        <span className="text-slate-600 font-medium">
                                          Dzień {item.dayNumber}
                                        </span>
                                        <span className="ml-2 font-medium">
                                          {formatItemName(item)}
                                        </span>
                                      </div>
                                      <div className="text-right">
                                        {quantity}
                                      </div>
                                      <div className="text-right">
                                        {formatCurrency(unitPriceNet)}
                                      </div>
                                      <div className="text-right">
                                        {formatCurrency(totalItemPrice)}
                                      </div>
                                      <div className="text-right">
                                        {formatCurrency(grossItemPrice)}
                                      </div>
                                    </div>

                                    {/* Discount row (only if discount exists) */}
                                    {discountAmount > 0 && (
                                      <div className="grid grid-cols-[40px_2fr_60px_100px_100px_100px] text-xs text-green-700 py-1 bg-green-50 rounded">
                                        <div></div>
                                        <div className="text-left pl-4">
                                          └─ {discountDescription}
                                        </div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>

                          {/* Global discounts section - only for accommodation categories */}
                          {(group.categoryName
                            .toLowerCase()
                            .includes("noclegi") ||
                            group.categoryName
                              .toLowerCase()
                              .includes("accommodation")) &&
                            hasDiscount && (
                              <div className="mt-4 pt-3 border-t border-slate-200">
                                <div className="text-xs font-medium text-slate-700 mb-2">
                                  Zastosowane rabaty na noclegi:
                                </div>
                                <div className="space-y-1">
                                  {/* Person discount */}
                                  {appliedDiscounts.personDiscount > 0 && (
                                    <div className="grid grid-cols-[2fr_1fr] text-xs text-green-700 py-1">
                                      <div className="text-left">
                                        └─ Rabat za liczbę osób (≥
                                        {appliedDiscounts.personDiscountDetails
                                          ?.appliedTier?.minPersons || 0}{" "}
                                        os., {appliedDiscounts.personDiscount}%)
                                      </div>
                                      <div className="text-right font-medium">
                                        -
                                        {formatCurrency(
                                          (accommodationOriginalNet *
                                            appliedDiscounts.personDiscount) /
                                            100
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Stay length discount */}
                                  {appliedDiscounts.stayLengthDiscount > 0 && (
                                    <div className="grid grid-cols-[2fr_1fr] text-xs text-green-700 py-1">
                                      <div className="text-left">
                                        └─ Rabat za długość pobytu (≥
                                        {appliedDiscounts
                                          .stayLengthDiscountDetails
                                          ?.appliedTier?.minNights || 0}{" "}
                                        noce,{" "}
                                        {appliedDiscounts.stayLengthDiscount}%)
                                      </div>
                                      <div className="text-right font-medium">
                                        -
                                        {formatCurrency(
                                          (accommodationOriginalNet *
                                            appliedDiscounts.stayLengthDiscount) /
                                            100
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Weekday discount */}
                                  {appliedDiscounts.weekdayDiscount > 0 && (
                                    <div className="grid grid-cols-[2fr_1fr] text-xs text-green-700 py-1">
                                      <div className="text-left">
                                        └─ Rabat za pobyt w tygodniu (
                                        {appliedDiscounts.weekdayDiscount}%)
                                      </div>
                                      <div className="text-right font-medium">
                                        -
                                        {formatCurrency(
                                          (accommodationOriginalNet *
                                            appliedDiscounts.weekdayDiscount) /
                                            100
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Custom discounts */}
                                  {appliedDiscounts.customDiscounts &&
                                    appliedDiscounts.customDiscounts
                                      .filter((cd) => cd.percentage > 0)
                                      .map((cd, idx) => (
                                        <div
                                          key={idx}
                                          className="grid grid-cols-[2fr_1fr] text-xs text-green-700 py-1"
                                        >
                                          <div className="text-left">
                                            └─ {cd.name} ({cd.percentage}%)
                                          </div>
                                          <div className="text-right font-medium">
                                            -
                                            {formatCurrency(
                                              (accommodationOriginalNet *
                                                cd.percentage) /
                                                100
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Table footer with totals - UPDATED LAYOUT */}
            <div
              className="text-white"
              style={{
                background: `linear-gradient(135deg, var(--gradient-start, #334155) 0%, var(--gradient-end, #1e293b) 100%)`,
              }}
            >
              {/* Summary before discounts. When a discount applies this is the "before"
                  (struck-through) row. When there is no discount this row IS the final total,
                  so it adopts the final-total styling (text-xl, full white) and drops its own
                  bottom border to avoid a doubled separator with the section below. */}
              <div
                data-testid="grand-total-row"
                className={`grid grid-cols-[80px_2fr_1fr_1fr_1fr] font-bold ${hasDiscount ? "text-lg border-b border-white border-opacity-20" : "text-xl"}`}
              >
                <div className="p-4 text-left col-span-2">
                  <div>RAZEM:</div>
                </div>
                <div
                  className={`p-4 text-right ${hasDiscount ? "text-slate-300 line-through" : "text-white"}`}
                >
                  {formatCurrency(originalTotal)}
                </div>
                <div
                  className={`p-4 text-right ${hasDiscount ? "text-slate-300 line-through" : "text-white"}`}
                >
                  {formatCurrency(originalTotalVat)}
                </div>
                <div
                  className={`p-4 text-right ${hasDiscount ? "text-slate-300 line-through" : "text-white"}`}
                >
                  {formatCurrency(originalTotalGross)}
                </div>
              </div>

              {/* Discount row (if any) */}
              {hasDiscount && (
                <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-lg font-bold border-b border-white border-opacity-20 bg-green-600 bg-opacity-20">
                  <div className="p-4 text-left col-span-2">
                    <div>Łączna oszczędność:</div>
                    <div className="text-sm font-medium text-green-300 mt-1">
                      (
                      {((totalDiscountAmount / originalTotal) * 100).toFixed(1)}
                      % wartości zamówienia)
                    </div>
                  </div>
                  <div className="p-4 text-right text-green-300">
                    -{formatCurrency(totalDiscountAmount)}
                  </div>
                  <div className="p-4 text-right"></div>
                  <div className="p-4 text-right"></div>
                </div>
              )}

              {/* Final totals after discounts. Only meaningful when a discount applies —
                  otherwise it would duplicate the "RAZEM:" row above to the penny, so we hide
                  it and let "RAZEM:" stand as the single final total. */}
              {hasDiscount && (
                <div
                  data-testid="discount-total-row"
                  className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-xl font-bold"
                >
                  <div className="p-4 text-left col-span-2">
                    <div>RAZEM PO RABATACH:</div>
                  </div>
                  <div className="p-4 text-right">
                    {formatCurrency(totalNetto)}
                  </div>
                  <div className="p-4 text-right">
                    {formatCurrency(totalVat)}
                  </div>
                  <div className="p-4 text-right">
                    {formatCurrency(totalBrutto)}
                  </div>
                </div>
              )}

              {/* Alternative VAT calculations */}
              <div className="border-t border-white border-opacity-20">
                <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-sm text-slate-200 pt-2 pb-2">
                  <div className="p-4 text-left col-span-2">
                    <span>Suma brutto (gdyby wszystko 23% VAT)</span>
                  </div>
                  <div className="p-4 text-right"></div>
                  <div className="p-4 text-right"></div>
                  <div className="p-4 text-right font-semibold">
                    {formatCurrency(totalWith23Vat)}
                  </div>
                </div>

                {/* Price per person section - moved here to be directly under 23% VAT */}
                {financialSummary.pricePerPerson && (
                  <>
                    <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr] text-sm text-slate-200 pt-2 pb-2">
                      <div className="p-4 text-left col-span-2">
                        <span>
                          Cena na osobę (
                          {financialSummary.pricePerPerson.personsCount} os.)
                        </span>
                      </div>
                      <div className="p-4 text-right font-semibold">
                        {formatCurrency(
                          financialSummary.pricePerPerson.netPerPerson
                        )}
                      </div>
                      <div className="p-4 text-right"></div>
                      <div className="p-4 text-right font-semibold">
                        {formatCurrency(
                          financialSummary.pricePerPerson.grossPerPerson
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PublicFinancialSummary;
